// Einstellungen → Onboarding-Baukasten: freier Drag-&-Drop-Editor, mit dem das
// Studio den Onboarding-Ablauf seiner Endkunden zusammenstellt. System-Schritte
// (Stammdaten/Angebote/Einverständnis) und beliebig viele eigene Schritte mit
// frei definierten Feldern, per Drag-&-Drop sortierbar.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Plus, Trash2, Save, Loader2, ListChecks, ChevronUp, ChevronDown, Workflow, Eye } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SYSTEM_LABELS = { profile: 'Stammdaten', offers: 'Angebote', consents: 'Einverständnis' };
const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Textbereich' },
  { value: 'number', label: 'Zahl' },
  { value: 'date', label: 'Datum' },
  { value: 'select', label: 'Auswahlliste' },
  { value: 'multiselect', label: 'Mehrfachauswahl' },
  { value: 'boolean', label: 'Ja / Nein' },
  { value: 'email', label: 'E-Mail' },
  { value: 'phone', label: 'Telefon' },
  { value: 'heading', label: 'Überschrift / Info' },
  { value: 'info', label: 'Infotext (nur Anzeige)' },
  { value: 'upload', label: 'Datei-Upload (PDF/Foto)' },
  { value: 'signature', label: 'Unterschrift' },
];
const hasOptions = (t) => t === 'select' || t === 'multiselect';
const isDisplayOnly = (t) => t === 'heading' || t === 'info';
const slugKey = (label) => {
  const s = (label || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  return s;
};
const uid = () => `tmp_${Math.random().toString(36).slice(2, 9)}`;

const SortableStep = ({ step, onPatch, onDelete, onAddField, onPatchField, onDeleteField, onMoveField }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: step.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  const isSystem = step.type !== 'custom';

  return (
    <div ref={setNodeRef} style={style} data-testid={`ob-step-${step.id}`}>
      <Card className={step.enabled ? '' : 'opacity-60'}>
        <CardContent className="pt-4 space-y-3">
          <div className="flex items-center gap-2">
            <button className="cursor-grab text-muted-foreground touch-none" {...attributes} {...listeners} data-testid={`ob-step-drag-${step.id}`}>
              <GripVertical className="w-5 h-5" />
            </button>
            <span className={`text-[11px] px-2 py-0.5 rounded-full ${isSystem ? 'bg-slate-100 text-slate-600' : 'bg-indigo-100 text-indigo-700'}`}>
              {isSystem ? `System · ${SYSTEM_LABELS[step.type]}` : 'Eigener Schritt'}
            </span>
            <Input className="flex-1" value={step.title} onChange={(e) => onPatch({ title: e.target.value })} placeholder="Schritt-Titel" data-testid={`ob-step-title-${step.id}`} />
            <label className="flex items-center gap-1.5 text-sm whitespace-nowrap">
              <Switch checked={step.enabled} onCheckedChange={(c) => onPatch({ enabled: c })} data-testid={`ob-step-enabled-${step.id}`} /> Aktiv
            </label>
            {!isSystem && (
              <Button variant="outline" size="sm" className="text-red-600" onClick={onDelete} data-testid={`ob-step-delete-${step.id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>

          <Input value={step.description || ''} onChange={(e) => onPatch({ description: e.target.value })} placeholder="Beschreibung (optional)" data-testid={`ob-step-desc-${step.id}`} />

          {isSystem ? (
            <p className="text-xs text-muted-foreground">Systemschritt — Inhalt wird automatisch gefüllt (Stammdaten-Formular, Probe-Angebote bzw. Einverständnis-Unterschrift).</p>
          ) : (
            <div className="space-y-2 border-t pt-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold">Felder</Label>
                <Button variant="ghost" size="sm" onClick={onAddField} data-testid={`ob-step-addfield-${step.id}`}><Plus className="w-4 h-4 mr-1" /> Feld</Button>
              </div>
              {(step.fields || []).length === 0 && <p className="text-xs text-muted-foreground">Noch keine Felder. Füge welche hinzu.</p>}
              {(step.fields || []).map((f, fi) => (
                <div key={f.id} className="rounded-md border bg-muted/30 p-2 space-y-2" data-testid={`ob-field-${f.id}`}>
                  <div className="flex items-center gap-2">
                    <div className="flex flex-col">
                      <button onClick={() => onMoveField(fi, -1)} disabled={fi === 0} className="text-muted-foreground disabled:opacity-30"><ChevronUp className="w-3.5 h-3.5" /></button>
                      <button onClick={() => onMoveField(fi, 1)} disabled={fi === step.fields.length - 1} className="text-muted-foreground disabled:opacity-30"><ChevronDown className="w-3.5 h-3.5" /></button>
                    </div>
                    <Input className="flex-1" value={f.label} onChange={(e) => onPatchField(fi, { label: e.target.value })} placeholder="Feld-Bezeichnung" data-testid={`ob-field-label-${f.id}`} />
                    <Select value={f.type} onValueChange={(v) => onPatchField(fi, { type: v })}>
                      <SelectTrigger className="w-40" data-testid={`ob-field-type-${f.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FIELD_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {!isDisplayOnly(f.type) && (
                      <label className="flex items-center gap-1 text-xs whitespace-nowrap">
                        <Switch checked={!!f.required} onCheckedChange={(c) => onPatchField(fi, { required: c })} data-testid={`ob-field-required-${f.id}`} /> Pflicht
                      </label>
                    )}
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => onDeleteField(fi)} data-testid={`ob-field-delete-${f.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                  {hasOptions(f.type) && (
                    <Input value={(f.options || []).join(', ')} onChange={(e) => onPatchField(fi, { options: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} placeholder="Optionen mit Komma trennen" data-testid={`ob-field-options-${f.id}`} />
                  )}
                  {!isDisplayOnly(f.type) && (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[11px] text-muted-foreground">Nur anzeigen wenn</span>
                      <Select
                        value={f.visible_if?.field || '__none'}
                        onValueChange={(v) => onPatchField(fi, { visible_if: v === '__none' ? null : { field: v, equals: f.visible_if?.equals ?? '' } })}
                      >
                        <SelectTrigger className="h-8 w-44 text-xs" data-testid={`ob-field-cond-field-${f.id}`}><SelectValue placeholder="(immer anzeigen)" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none">(immer anzeigen)</SelectItem>
                          {(step.fields || []).filter((x, xi) => xi !== fi && x.key && !isDisplayOnly(x.type)).map((x) => (
                            <SelectItem key={x.key} value={x.key}>{x.label || x.key}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {f.visible_if?.field && (() => {
                        const src = (step.fields || []).find((x) => x.key === f.visible_if.field);
                        const opts = src && (src.type === 'select' || src.type === 'multiselect') ? (src.options || []) : (src && src.type === 'boolean' ? ['true', 'false'] : null);
                        if (opts) {
                          return (
                            <Select value={f.visible_if?.equals ?? ''} onValueChange={(v) => onPatchField(fi, { visible_if: { field: f.visible_if.field, equals: v } })}>
                              <SelectTrigger className="h-8 w-32 text-xs" data-testid={`ob-field-cond-value-${f.id}`}><SelectValue placeholder="= Wert" /></SelectTrigger>
                              <SelectContent>
                                {opts.map((o) => <SelectItem key={o} value={o}>{o === 'true' ? 'Ja' : o === 'false' ? 'Nein' : o}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          );
                        }
                        return <Input className="h-8 w-32 text-xs" value={f.visible_if?.equals ?? ''} onChange={(e) => onPatchField(fi, { visible_if: { field: f.visible_if.field, equals: e.target.value } })} placeholder="= Wert" data-testid={`ob-field-cond-value-${f.id}`} />;
                      })()}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const OnboardingBuilderSettingsPage = ({ embedded = false }) => {
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const load = async () => {
    try {
      const res = await axios.get(`${API}/settings/onboarding-builder`);
      setSteps(res.data?.steps || []);
    } catch {
      toast.error('Baukasten konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const patchStep = (id, patch) => setSteps((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  const deleteStep = (id) => setSteps((prev) => prev.filter((s) => s.id !== id));
  const addCustomStep = () => setSteps((prev) => [...prev, { id: uid(), type: 'custom', title: 'Neuer Schritt', description: '', enabled: true, fields: [] }]);
  const addField = (id) => patchStep(id, { fields: [...(steps.find((s) => s.id === id)?.fields || []), { id: uid(), label: '', type: 'text', required: false, options: [] }] });
  const patchField = (id, fi, patch) => {
    const st = steps.find((s) => s.id === id);
    const fields = st.fields.map((f, i) => {
      if (i !== fi) return f;
      const merged = { ...f, ...patch };
      // Auto-Schlüssel aus dem Label, solange noch keiner gespeichert ist (für Bedingungen vor dem ersten Speichern).
      if ('label' in patch && !f.key) merged.key = slugKey(patch.label);
      return merged;
    });
    patchStep(id, { fields });
  };
  const deleteField = (id, fi) => patchStep(id, { fields: steps.find((s) => s.id === id).fields.filter((_, i) => i !== fi) });
  const moveField = (id, fi, dir) => {
    const st = steps.find((s) => s.id === id);
    const to = fi + dir;
    if (to < 0 || to >= st.fields.length) return;
    patchStep(id, { fields: arrayMove(st.fields, fi, to) });
  };

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSteps((prev) => {
      const oldIdx = prev.findIndex((s) => s.id === active.id);
      const newIdx = prev.findIndex((s) => s.id === over.id);
      return arrayMove(prev, oldIdx, newIdx);
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings/onboarding-builder`, { steps });
      setSteps(res.data?.steps || []);
      toast.success('Onboarding-Ablauf gespeichert');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-muted-foreground p-6"><Loader2 className="w-4 h-4 animate-spin" /> Lädt …</div>;

  return (
    <div className="space-y-5" data-testid="onboarding-builder-settings">
      {!embedded && (
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-amber-100 text-amber-600"><Workflow className="w-5 h-5" /></div>
          <div>
            <h2 className="text-lg font-semibold">Onboarding-Baukasten</h2>
            <p className="text-sm text-muted-foreground">Stelle den Ablauf zusammen, den neue Kunden über ihren Onboarding-Link durchlaufen.</p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground flex items-center gap-1.5"><ListChecks className="w-4 h-4" /> Ziehe die Schritte in die gewünschte Reihenfolge.</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setPreviewOpen(true)} data-testid="ob-preview-btn"><Eye className="w-4 h-4 mr-2" /> Vorschau</Button>
          <Button variant="outline" onClick={addCustomStep} data-testid="ob-add-step"><Plus className="w-4 h-4 mr-2" /> Eigenen Schritt</Button>
        </div>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={steps.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {steps.map((s) => (
              <SortableStep
                key={s.id}
                step={s}
                onPatch={(p) => patchStep(s.id, p)}
                onDelete={() => deleteStep(s.id)}
                onAddField={() => addField(s.id)}
                onPatchField={(fi, p) => patchField(s.id, fi, p)}
                onDeleteField={(fi) => deleteField(s.id, fi)}
                onMoveField={(fi, dir) => moveField(s.id, fi, dir)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <div className="sticky bottom-0 bg-background/80 backdrop-blur py-3">
        <Button onClick={save} disabled={saving} data-testid="ob-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Ablauf speichern
        </Button>
      </div>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto bg-[#0a0a0a] text-white border-white/10" data-testid="ob-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2"><Eye className="w-5 h-5" /> Vorschau — so sieht es der Kunde</DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-1.5 flex-wrap mb-2">
            {steps.filter((s) => s.enabled).map((s, i, arr) => (
              <React.Fragment key={s.id}>
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${i === 0 ? 'bg-yellow-500 text-black' : 'bg-white/5 text-white/60'}`}>{s.title || '—'}</span>
                {i < arr.length - 1 && <span className="text-white/30">›</span>}
              </React.Fragment>
            ))}
          </div>
          {steps.filter((s) => s.enabled).length === 0 && <p className="text-white/50 text-sm">Keine aktiven Schritte.</p>}
          {steps.filter((s) => s.enabled).map((s) => (
            <div key={s.id} className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3 mb-3" data-testid={`ob-preview-step-${s.id}`}>
              <div>
                <p className="font-semibold">{s.title || '(ohne Titel)'}</p>
                {s.description && <p className="text-white/50 text-xs">{s.description}</p>}
              </div>
              {s.type !== 'custom' ? (
                <p className="text-white/40 text-xs italic">
                  {s.type === 'profile' && 'Stammdaten-Formular (Name, Geburtsdatum, Adresse …)'}
                  {s.type === 'offers' && 'Auswahl der Probe-Angebote'}
                  {s.type === 'consents' && 'Einverständnis-Erklärungen zum Unterschreiben'}
                </p>
              ) : (
                <div className="space-y-2.5">
                  {(s.fields || []).length === 0 && <p className="text-white/40 text-xs">Noch keine Felder.</p>}
                  {(s.fields || []).map((f) => (
                    <div key={f.id}>
                      {f.type === 'heading' ? (
                        <p className="text-white/80 font-semibold text-sm">{f.label || 'Überschrift'}</p>
                      ) : (
                        <>
                          <p className="text-white/70 text-xs mb-1">{f.label || '(Feld)'}{f.required ? ' *' : ''}</p>
                          {(f.type === 'select' || f.type === 'multiselect') ? (
                            <div className="flex flex-wrap gap-1.5">
                              {(f.options || []).map((o) => <span key={o} className="px-2 py-0.5 rounded border border-white/15 text-xs text-white/60">{o}</span>)}
                              {(f.options || []).length === 0 && <span className="text-white/30 text-xs">keine Optionen</span>}
                            </div>
                          ) : f.type === 'boolean' ? (
                            <div className="flex gap-1.5"><span className="px-2 py-0.5 rounded border border-white/15 text-xs text-white/60">Ja</span><span className="px-2 py-0.5 rounded border border-white/15 text-xs text-white/60">Nein</span></div>
                          ) : (
                            <div className="h-8 rounded-md bg-white/5 border border-white/15" />
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OnboardingBuilderSettingsPage;
